import axiosClient from '../libs/axios';

// API'den gelen dosyayı frontend formatına çevir
export const formatFileFromApi = (apiFile) => {
  return {
    id: apiFile.id,
    projectId: apiFile.project_id,
    fileName: apiFile.file_name,
    originalName: apiFile.original_name,
    filePath: apiFile.file_path,
    fileType: apiFile.file_type,
    fileSize: apiFile.file_size,
    mimeType: apiFile.mime_type,
    folderPath: apiFile.folder_path,
    isFolder: apiFile.is_folder,
    parentFolderId: apiFile.parent_folder_id,
    uploadedBy: apiFile.uploaded_by,
    createdAt: apiFile.created_at,
    updatedAt: apiFile.updated_at,
    uploader: apiFile.uploader ? 
      `${apiFile.uploader.ufname} ${apiFile.uploader.ulname}` : '',
    // Frontend için ek alanlar
    name: apiFile.original_name,
    type: apiFile.is_folder ? 'folder' : apiFile.file_type,
    size: apiFile.is_folder ? null : formatFileSize(apiFile.file_size),
    modified: apiFile.updated_at ? apiFile.updated_at.split('T')[0] : '',
    path: apiFile.folder_path + (apiFile.folder_path === '/' ? '' : '/') + apiFile.original_name,
    parentPath: apiFile.parent_folder_id ? getParentPath(apiFile.folder_path) : '/'
  };
};

// Frontend formatından API formatına çevir
export const formatFileForApi = (file) => {
  return {
    folder_name: file.name,
    parent_folder_id: file.parentFolderId || null
  };
};

// Dosya boyutunu formatla
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '';
  
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

// Parent path'i hesapla
const getParentPath = (folderPath) => {
  if (folderPath === '/') return '/';
  const parts = folderPath.split('/').filter(part => part !== '');
  return '/' + parts.slice(0, -1).join('/');
};

// Proje dosyalarını getir
export const getProjectFiles = async (projectSlug, parentFolderId = null) => {
  try {
    const params = parentFolderId ? { parent_folder_id: parentFolderId } : {};
    const response = await axiosClient.get(`/files/${projectSlug}`, { params });
    return {
      files: response.data.files.map(formatFileFromApi),
      total: response.data.total
    };
  } catch (error) {
    throw error;
  }
};

// Dosya yükle
export const uploadFile = async (projectSlug, file, parentFolderId = null) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (parentFolderId) {
      formData.append('parent_folder_id', parentFolderId);
    }

    const response = await axiosClient.post(`/files/upload/${projectSlug}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return {
      message: response.data.message,
      file: formatFileFromApi(response.data.file)
    };
  } catch (error) {
    throw error;
  }
};

// Klasör oluştur
export const createFolder = async (projectSlug, folderData) => {
  try {
    const formattedData = formatFileForApi(folderData);
    const response = await axiosClient.post(`/files/folder/${projectSlug}`, formattedData);
    return {
      message: response.data.message,
      folder: formatFileFromApi(response.data.folder)
    };
  } catch (error) {
    throw error;
  }
};

// Dosya/Klasörü sil
export const deleteFile = async (fileId) => {
  try {
    const response = await axiosClient.delete(`/files/${fileId}`);
    return {
      message: response.data.message
    };
  } catch (error) {
    throw error;
  }
};

// Dosya indir
export const downloadFile = async (fileId) => {
  try {
    const response = await axiosClient.get(`/files/download/${fileId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Dosya ara
export const searchFiles = async (projectSlug, searchTerm) => {
  try {
    const response = await axiosClient.get(`/files/search/${projectSlug}`, {
      params: { q: searchTerm }
    });
    return {
      files: response.data.files.map(formatFileFromApi),
      total: response.data.total,
      searchTerm: response.data.search_term
    };
  } catch (error) {
    throw error;
  }
};

// Dosya türü ikonları
export const getFileIcon = (fileType, fileName) => {
  if (fileType === 'folder') {
    return 'folder';
  }
  
  const extension = fileName.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'pdf':
      return 'pdf';
    case 'doc':
    case 'docx':
      return 'word';
    case 'xls':
    case 'xlsx':
      return 'excel';
    case 'ppt':
    case 'pptx':
      return 'powerpoint';
    case 'zip':
    case 'rar':
      return 'archive';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
      return 'image';
    case 'mp4':
    case 'avi':
    case 'mov':
      return 'video';
    case 'txt':
    case 'csv':
      return 'text';
    default:
      return 'file';
  }
};

// Dosya türü renkleri
export const getFileTypeColor = (fileType, fileName) => {
  if (fileType === 'folder') {
    return 'text-blue-500';
  }
  
  const extension = fileName.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'pdf':
      return 'text-red-500';
    case 'doc':
    case 'docx':
      return 'text-blue-600';
    case 'xls':
    case 'xlsx':
      return 'text-green-600';
    case 'ppt':
    case 'pptx':
      return 'text-orange-500';
    case 'zip':
    case 'rar':
      return 'text-purple-500';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
      return 'text-pink-500';
    case 'mp4':
    case 'avi':
    case 'mov':
      return 'text-indigo-500';
    case 'txt':
    case 'csv':
      return 'text-gray-500';
    default:
      return 'text-gray-500';
  }
};

// Desteklenen dosya türleri
export const SUPPORTED_FILE_TYPES = {
  IMAGES: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
  DOCUMENTS: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'],
  TEXT: ['txt', 'csv'],
  ARCHIVES: ['zip', 'rar'],
  VIDEOS: ['mp4', 'avi', 'mov']
};

// Maksimum dosya boyutu (50MB)
export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes

// Dosya türü kontrolü
export const isFileTypeSupported = (fileName) => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  const allSupportedTypes = [
    ...SUPPORTED_FILE_TYPES.IMAGES,
    ...SUPPORTED_FILE_TYPES.DOCUMENTS,
    ...SUPPORTED_FILE_TYPES.TEXT,
    ...SUPPORTED_FILE_TYPES.ARCHIVES,
    ...SUPPORTED_FILE_TYPES.VIDEOS
  ];
  return allSupportedTypes.includes(extension);
};
